/**
 * @file deal-columns.tsx
 * @description Колонки реестра сделок для `ResponsiveList` (phase19.31, зона
 * «Клиенты и сделки», макет (в); SC46).
 *
 * Одно описание — две раскладки:
 *  - от 1024 таблица: Сделка (название + клиент второй линией) · Воронка ·
 *    Стадия (пилюля по виду стадии, цвет магазина — точкой) · Сумма (справа,
 *    табличные цифры, «128 000 ₽») · Ответственный · Срок (просрочка —
 *    янтарным «▲ 12 дн · до 20.09»). Строка в две линии, вся — ссылка;
 *  - на 1024–1279 «Воронка» и «Ответственный» уходят второй линией под
 *    стадию одной строкой «Розничные · А. Кравец» (`fold`): таблица влезает в
 *    блок без прокрутки. Вторая свёрнутая колонка пишет пустую линию — у
 *    пустой строки нет высоты, строка остаётся в две линии;
 *  - с 1280 «Воронка» и «Ответственный» — столбцами по ширине содержимого
 *    («Корпоративные продажи», «Администратор Chaiberi» целиком, длиннее —
 *    переносом, не многоточием); «Сделка» — гибкая, название до двух строк
 *    (приёмка QA-5 D4);
 *  - до 1024 компактный список (длинное название — до двух строк, срок
 *    переносится, а не уходит за край): «название … сумма» / «клиент · воронка» /
 *    «стадия · ответственный · просрочка».
 */

import { DealDeadlineCell, OverdueMark } from "./DealDeadline";
import { DealStagePill } from "../DealStagePill";
import type { ListColumn } from "../../ui/responsive-list-types";
import { TwoLineCell } from "../../ui/TwoLineCell";
import type { DealDto } from "../../../lib/api-crm";
import { dealDeadline, formatRub } from "../../../lib/crm-deal-view";

/** Ответственный словами; без него — «не назначен». */
function ownerText(deal: DealDto): string {
  return deal.ownerDisplayName ?? "не назначен";
}

/** Сумма: целая — без копеек, пустая — прочерк. */
function amountCell(deal: DealDto) {
  return <span className="tabular-nums whitespace-nowrap">{formatRub(deal.expectedAmountGross, "whole")}</span>;
}

/** Просрочка для компактной строки; иначе ничего (срок в срок на телефоне не нужен). */
function overdueCompact(deal: DealDto) {
  const state = dealDeadline(deal);
  return state.kind === "overdue" ? <OverdueMark text={state.overdue} /> : null;
}

export const DEAL_REGISTRY_COLUMNS: ListColumn<DealDto>[] = [
  {
    key: "deal",
    header: "Сделка",
    cell: (d) => (
      <TwoLineCell
        wrapPrimary
        primary={<span className="font-medium" title={d.displayName}>{d.displayName}</span>}
        secondary={d.customerDisplayName ?? "Клиент не указан"}
      />
    ),
    compact: "title",
    compactCell: (d) => d.displayName,
  },
  {
    key: "customer",
    header: "Клиент",
    compactOnly: true,
    cell: (d) => d.customerDisplayName ?? "Клиент не указан",
    compact: "subtitle",
  },
  {
    key: "pipeline",
    header: "Воронка",
    width: "w-[184px]",
    cell: (d) => <span className="block break-words">{d.pipelineName}</span>,
    compact: "subtitle",
    compactCell: (d) => d.pipelineName,
    fold: { into: "stage", cell: (d) => `${d.pipelineName} · ${ownerText(d)}` },
  },
  {
    key: "stage",
    header: "Стадия",
    width: "w-[164px]",
    cell: (d) => <DealStagePill deal={d} />,
    compact: "meta",
  },
  {
    key: "amount",
    header: "Сумма",
    width: "w-[112px]",
    align: "end",
    cell: amountCell,
    compact: "trailing",
    compactCell: (d) => <span className="type-body font-semibold tabular-nums whitespace-nowrap">{formatRub(d.expectedAmountGross, "whole")}</span>,
  },
  {
    key: "owner",
    header: "Ответственный",
    width: "w-[184px]",
    cell: (d) => <span className="block break-words">{ownerText(d)}</span>,
    compact: "meta",
    compactCell: ownerText,
    // Линия уже есть у «Воронки» (обе в одной строке под стадией).
    fold: { into: "stage", cell: () => null },
  },
  {
    key: "deadline",
    header: "Срок",
    width: "w-[108px]",
    cell: (d) => <DealDeadlineCell deal={d} />,
    compact: "meta",
    compactCell: overdueCompact,
  },
];
